import {
  SymptomTicketUrgency,
  SymptomTicketUrgencyText,
  AlertUrgency,
  AlertAttention,
  OperatorTicketUrgency,
  OperatorTicketUrgencyText
} from 'constants/urgency.consts';

import { PathwayOptionOutcome } from 'models/PathwayTemplates';

export interface SymptomTicketUrgencyValueAndText {
  value: SymptomTicketUrgency;
  label: SymptomTicketUrgencyText;
}

export const defaultSymptomUrgencyValue: SymptomTicketUrgencyValueAndText = {
  value: SymptomTicketUrgency.NurseReview,
  label: SymptomTicketUrgencyText.NurseReview
};

export const defaultNonSymptomUrgencyValue = {
  value: OperatorTicketUrgency.Standard,
  label: OperatorTicketUrgencyText.Standard
};

// Mapping from symptom ticket urgency to pathway option outcome
export const urgencyToQuestionOutcomeMap: Partial<
  Record<SymptomTicketUrgency, PathwayOptionOutcome>
> = {
  [SymptomTicketUrgency.NurseReview]: PathwayOptionOutcome.NONE,
  [SymptomTicketUrgency.AttentionToday]: PathwayOptionOutcome.CLINIC,
  [SymptomTicketUrgency.ImmediateAttention]: PathwayOptionOutcome.CLINIC_EMERGENT
};

// Individual urgency values and text representations
export const nurseReviewUrgency: SymptomTicketUrgencyValueAndText = {
  value: SymptomTicketUrgency.NurseReview,
  label: SymptomTicketUrgencyText.NurseReview
};

export const attentionTodayUrgency: SymptomTicketUrgencyValueAndText = {
  value: SymptomTicketUrgency.AttentionToday,
  label: SymptomTicketUrgencyText.AttentionToday
};

export const immediateAttentionUrgency: SymptomTicketUrgencyValueAndText = {
  value: SymptomTicketUrgency.ImmediateAttention,
  label: SymptomTicketUrgencyText.ImmediateAttention
};

// Mapping from pathway option outcome to symptom ticket urgency
export const questionOutcomeToUrgencyMap: Partial<
  Record<PathwayOptionOutcome, SymptomTicketUrgencyValueAndText>
> = {
  [PathwayOptionOutcome.NONE]: nurseReviewUrgency,
  [PathwayOptionOutcome.CLINIC]: attentionTodayUrgency,
  [PathwayOptionOutcome.CLINIC_EMERGENT]: immediateAttentionUrgency
};

// Mapping from alert urgency to symptom ticket urgency values and text
export const alertUrgencyToTextAndValueMap: Partial<
  Record<AlertUrgency, SymptomTicketUrgencyValueAndText>
> = {
  [AlertUrgency.UrgentAlert]: attentionTodayUrgency,
  [AlertUrgency.CriticalAlert]: immediateAttentionUrgency
};

export const symptomTicketUrgencyToAlertAttentionMap: Record<SymptomTicketUrgency, AlertAttention> =
  {
    [SymptomTicketUrgency.NurseReview]: AlertAttention.NurseReview,
    [SymptomTicketUrgency.AttentionToday]: AlertAttention.AttentionToday,
    [SymptomTicketUrgency.ImmediateAttention]: AlertAttention.ImmediateAttention
  };

// Mapping from alert urgency to alert attention
export const alertAttentionByUrgency: Record<AlertUrgency, AlertAttention> = {
  [AlertUrgency.Alert]: AlertAttention.NurseReview,
  [AlertUrgency.UrgentAlert]: AlertAttention.AttentionToday,
  [AlertUrgency.CriticalAlert]: AlertAttention.ImmediateAttention
};

// Helper function to determine if urgency should be updated
export const shouldUpdateUrgency = (
  alertUrgency: AlertUrgency,
  currentHighest: AlertUrgency | null
): boolean => {
  return (
    currentHighest === null ||
    alertUrgency === AlertUrgency.CriticalAlert ||
    (alertUrgency === AlertUrgency.UrgentAlert && currentHighest !== AlertUrgency.CriticalAlert)
  );
};
